<template>
  <div class="flex min-h-screen bg-background">
    <!-- Sidebar with steps -->
    <div class="w-64 bg-secondary border-r border-secondary-light hidden md:block">
      <div class="h-full flex flex-col">
        <div class="p-4 border-b border-secondary-light flex items-center">
          <div class="flex-shrink-0 flex items-center">
            <img src="~/assets/img/loop-letters-logo.svg" class="h-[40px] w-[40px]" alt="Logo" />
            <h1 class="text-lg font-bold text-text ml-2">{{ $appTitle }}</h1>
          </div>
        </div>
        <div class="flex-1 overflow-y-auto py-4 px-3">
          <ol class="relative border-l border-secondary-light">
            <li v-for="(step, index) in wizardSteps" :key="index" class="mb-6 ml-4">
              <div :class="`absolute w-6 h-6 rounded-full mt-1.5 -left-3 flex items-center justify-center ${
                activeStep > index 
                  ? 'bg-primary text-white' 
                  : activeStep === index 
                    ? 'bg-primary-light text-white border-primary' 
                    : 'bg-secondary-light text-text-secondary border border-secondary-light'
              }`">
                <span v-if="activeStep > index">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                  </svg>
                </span>
                <span v-else>{{ index + 1 }}</span>
              </div>
              <h3 :class="`ml-1 text-sm font-medium ${
                activeStep >= index ? 'text-text' : 'text-text-secondary'
              }`">
                {{ step.title }}
              </h3>
              <p :class="`mt-1 ml-1 text-xs ${
                activeStep >= index ? 'text-text-secondary' : 'text-text-secondary opacity-70'
              }`">
                {{ step.description }}
              </p>
            </li>
          </ol>
        </div>

        <div class="p-4 border-t border-secondary-light">
          <NuxtLink to="/dashboard" class="flex items-center text-text-secondary hover:text-text">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd" />
            </svg>
            Exit to Dashboard
          </NuxtLink>
        </div>
      </div>
    </div>

    <!-- Main content area -->
    <div class="flex-1 flex flex-col bg-background">
      <!-- Top navigation for mobile -->
      <div class="md:hidden bg-secondary shadow-sm border-b border-secondary-light">
        <div class="max-w-[1440px] mx-auto p-4">
          <div class="flex items-center justify-between">
            <div class="flex items-center">
              <img src="~/assets/img/loop-letters-logo.svg" class="h-[40px] w-[40px]" alt="Logo" />
              <h1 class="text-lg font-bold text-text ml-2">{{ wizardTitle }}</h1>
            </div>
            <NuxtLink to="/" class="text-text-secondary hover:text-text">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </NuxtLink>
          </div>
          <div class="mt-2 text-sm text-text-secondary">
            Step {{ activeStep + 1 }} of {{ wizardSteps.length }}: {{ wizardSteps[activeStep]?.title }}
          </div>
        </div>
      </div>

      <!-- Main content -->
      <div class="flex-1 overflow-y-auto">
        <div class="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <slot />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';

// Get app title from the plugin
const { $appTitle } = useNuxtApp();

// Wizard configuration
const activeStep = ref(0);

const wizardTitle = computed(() => {
  return 'Campaign Setup Wizard';
});

const wizardSteps = [
  { 
    title: 'Campaign Details', 
    description: 'Set name, sender, and basic campaign information',
  },
  { 
    title: 'Select Template', 
    description: 'Choose or create an email template',
  },
  { 
    title: 'Schedule', 
    description: 'Set dates, times and follow-up settings',
  },
  { 
    title: 'Recipients', 
    description: 'Add contacts or import a list',
  },
  { 
    title: 'Review & Launch', 
    description: 'Check settings and start your campaign',
  },
];

// Make these available to child components through provide/inject
provide('wizardState', {
  activeStep,
  wizardSteps,
  setStep: (step) => {
    activeStep.value = step;
  },
  nextStep: () => {
    if (activeStep.value < wizardSteps.length - 1) {
      activeStep.value++;
    }
  },
  prevStep: () => {
    if (activeStep.value > 0) {
      activeStep.value--;
    }
  }
});
</script> 